import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { ProfilePatch } from '@/api/endpoints'
import { usersApi } from '@/api/endpoints'
import { keys } from '@/api/keys'
import type { Page, User } from '@/api/types'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'
import { patchUserEverywhere } from '@/lib/cache'

export function useProfile(username: string) {
  return useQuery({
    queryKey: keys.user(username),
    queryFn: () => usersApi.get(username),
    enabled: Boolean(username),
  })
}

const pageOptions = {
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last: Page<User>) => last.next_cursor ?? undefined,
}

export function useFollowers(username: string) {
  return useInfiniteQuery({
    queryKey: keys.userFollowers(username),
    queryFn: ({ pageParam }) => usersApi.followers(username, pageParam),
    enabled: Boolean(username),
    ...pageOptions,
  })
}

export function useFollowing(username: string) {
  return useInfiniteQuery({
    queryKey: keys.userFollowing(username),
    queryFn: ({ pageParam }) => usersApi.following(username, pageParam),
    enabled: Boolean(username),
    ...pageOptions,
  })
}

export function useSuggestions() {
  return useQuery({
    queryKey: keys.suggestions(),
    queryFn: () => usersApi.suggestions(),
    staleTime: 5 * 60 * 1000,
  })
}

/** 关注 / 取关的乐观更新：按钮立刻变，失败回滚。 */
export function useFollowToggle() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    /**
     * `following` 为 true 时取关；对受保护账号（`isPrivate`）还没被批准的请求，
     * 也是走同一个 DELETE 撤回（D76）。
     */
    mutationFn: ({
      username,
      following,
    }: {
      username: string
      following: boolean
      isPrivate?: boolean
    }) => (following ? usersApi.unfollow(username) : usersApi.follow(username)),

    onMutate: async ({ username, following, isPrivate = false }) => {
      await client.cancelQueries({ queryKey: keys.user(username) })
      const snapshot = client.getQueriesData({ queryKey: ['user'] })

      patchUserEverywhere(client, username, (user) => {
        if (!user.viewer) return user
        // 受保护账号：关注只是发出请求，粉丝数不动
        if (isPrivate && !user.viewer.following) {
          return { ...user, viewer: { ...user.viewer, follow_requested: !following } }
        }
        return {
          ...user,
          counts: {
            ...user.counts,
            followers: Math.max(0, user.counts.followers + (following ? -1 : 1)),
          },
          viewer: { ...user.viewer, following: !following },
        }
      })

      return { snapshot }
    },

    onError: (_error, _vars, context) => {
      context?.snapshot.forEach(([key, data]) => client.setQueryData(key, data))
      toast.error(t('toast.actionFailed'))
    },

    onSuccess: (fresh) => {
      patchUserEverywhere(client, fresh.username, (user) => ({
        ...user,
        counts: fresh.counts,
        viewer: fresh.viewer ?? user.viewer,
      }))
      // 关注关系变了，主页时间线内容要换
      client.invalidateQueries({ queryKey: keys.timelineHome() })
      client.invalidateQueries({ queryKey: keys.suggestions() })
    },
  })
}

export function useUpdateProfile() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: (payload: ProfilePatch) => usersApi.updateMe(payload),
    onSuccess: (user) => {
      client.setQueryData(keys.me(), user)
      client.setQueryData(keys.user(user.username), user)
      toast.show(t('editProfile.saved'))
    },
  })
}

// ---- 关注请求（D76）----

export function useFollowRequests() {
  return useInfiniteQuery({
    queryKey: keys.followRequests(),
    queryFn: ({ pageParam }) => usersApi.followRequests(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: Page<User>) => last.next_cursor ?? undefined,
  })
}

export function useRespondFollowRequest() {
  const t = useT()
  const client = useQueryClient()
  const toast = useToast()

  return useMutation({
    mutationFn: ({ username, approve }: { username: string; approve: boolean }) =>
      approve ? usersApi.approveFollowRequest(username) : usersApi.rejectFollowRequest(username),
    onSuccess: ({ count }, { username, approve }) => {
      client.setQueryData<User | null>(keys.me(), (me) =>
        me ? { ...me, follow_requests_count: count } : me,
      )
      client.invalidateQueries({ queryKey: keys.followRequests() })
      toast.show(
        approve
          ? t('followRequests.approved', { username })
          : t('followRequests.rejected', { username }),
      )
    },
    onError: () => toast.error(t('toast.actionFailed')),
  })
}
